/* Builds the two card faces as SVG, in millimetre user units.

   This is the only place that draws anything. The preview shows exactly these
   nodes; the exporter takes the same nodes and swaps <text> for outlined
   <path>. Every text node carries the layout it was built with in data-*
   attributes so the exporter can reproduce it without re-deriving anything. */

window.JCARD = window.JCARD || {};

JCARD.render = (function () {
  const NS = 'http://www.w3.org/2000/svg';
  const XLINK = 'http://www.w3.org/1999/xlink';

  function el(tag, attrs) {
    const node = document.createElementNS(NS, tag);
    Object.keys(attrs || {}).forEach((k) => {
      if (attrs[k] === null || attrs[k] === undefined) return;
      node.setAttribute(k, attrs[k]);
    });
    return node;
  }

  function svgRoot(width, height) {
    const svg = el('svg', {
      xmlns: NS,
      viewBox: `0 0 ${width} ${height}`,
      width: `${width}mm`,
      height: `${height}mm`,
      'font-kerning': 'none',
    });
    return svg;
  }

  /* One run of text. `rot` of 90 turns it to read down the card, which is how
     the spine, flap and track columns are set. */
  function textNode(spec) {
    const node = el('text', {
      x: spec.x,
      y: spec.y,
      'font-family': `'${spec.font}'`,
      'font-weight': spec.weight,
      'font-size': spec.size,
      'letter-spacing': (spec.tracking || 0) * spec.size,
      'text-anchor': spec.anchor || 'start',
      fill: spec.color,
      'font-kerning': 'none',
      'xml:space': 'preserve',
    });
    node.textContent = spec.text;

    if (spec.rot) node.setAttribute('transform', `rotate(${spec.rot} ${spec.x} ${spec.y})`);
    if (spec.field) {
      node.setAttribute('data-field', spec.field);
      node.classList.add('jc-hit');
    }
    // Consumed by the exporter.
    node.setAttribute('data-font', spec.font);
    node.setAttribute('data-weight', spec.weight);
    node.setAttribute('data-size', spec.size);
    node.setAttribute('data-tracking', spec.tracking || 0);
    node.setAttribute('data-anchor', spec.anchor || 'start');
    return node;
  }

  /* Text never wraps and never spills. A line that would overrun the space it
     has is set at a smaller size instead, using the same advance widths the
     PDF outliner uses — so what fits on screen fits in the print. */
  function fit(field, text, maxWidth) {
    const w = JCARD.fonts.measure(field.font, field.weight, text, field.size, field.tracking);
    if (!w || w <= maxWidth) return field.size;
    return field.size * (maxWidth / w);
  }

  function linesOf(field, id) {
    const max = (JCARD.state.FIELD_META[id] || {}).maxLines || 1;
    return String(field.text).split('\n').slice(0, max).filter((l, i) => i === 0 || l.length);
  }

  /* Size for the block as a whole, so the stack of lines fits the depth it has
     to live in. `lead` is how much of that depth one line needs beyond the
     gaps: a centred block has to allow for a full glyph height, a block that
     only grows downward from its first baseline just needs its descender. */
  function fitBlock(field, lineCount, crossLimit, lead) {
    const extent = ((lineCount - 1) * JCARD.geom.inside.lineHeight + lead) * field.size;
    if (!crossLimit || extent <= crossLimit) return field.size;
    return field.size * (crossLimit / extent);
  }

  /* Ink sits between the descender and the cap, as fractions of the type size.
     A rotated line's ink lies entirely to one side of its baseline, so these
     are what turn a wanted ink position into a baseline position. */
  const DESCENDER = 0.22;
  const CAP = 0.75;
  const INK_DEPTH = DESCENDER + CAP;

  /* Lines are spaced on the block size so the leading stays even, but any one
     line that is still too long for its run is set smaller on its own. */
  function layoutLines(field, lines, maxRun, blockSize) {
    return lines.map((line) => {
      const w = JCARD.fonts.measure(field.font, field.weight, line, blockSize, field.tracking);
      return { line, size: !w || w <= maxRun ? blockSize : blockSize * (maxRun / w) };
    });
  }

  /* A rotated run reads down the card, so successive lines step across it.
     The block is centred on the anchor, and held inside crossLimit — the
     width of the panel it sits in. */
  function rotatedLines(svg, field, id, place, maxRun, crossLimit) {
    const lines = linesOf(field, id);
    const lh = JCARD.geom.inside.lineHeight;
    const blockSize = fitBlock(field, lines.length, crossLimit, INK_DEPTH);
    // Shift the baselines so the ink band, not the baselines, ends up centred.
    const inkShift = (DESCENDER - CAP) / 2 * blockSize;
    layoutLines(field, lines, maxRun, blockSize).forEach((item, i) => {
      if (!item.line) return;
      const offset = ((lines.length - 1) / 2 - i) * lh * blockSize;
      svg.appendChild(textNode(Object.assign({}, field, {
        text: item.line,
        size: item.size,
        x: place.x + offset + inkShift,
        y: place.y,
        rot: 90,
        anchor: place.anchor,
        color: field.color,
        field: i === 0 ? id : null,
      })));
    });
  }

  function background(svg, state, w, h) {
    svg.appendChild(el('rect', { x: 0, y: 0, width: w, height: h, fill: state.colors.background }));
  }

  function foldGuides(svg, positions, h) {
    positions.forEach((x) => {
      svg.appendChild(el('line', {
        x1: x, y1: 0, x2: x, y2: h,
        stroke: '#7CA9DE',
        'stroke-width': 0.2,
        'stroke-dasharray': '1.6 1.2',
        class: 'jc-guide',
      }));
    });
  }

  /* ---------- page 1: front / outside ---------- */

  function coverBox(state) {
    const g = JCARD.geom;
    if (state.cover.placement === 'full') {
      return { x: g.OUT.coverX, y: 0, w: g.COVER_W, h: g.CARD_H };
    }
    if (state.cover.placement === 'bleed') {
      return { x: g.OUT.coverX, y: 0, w: g.COVER_W, h: g.outside.image.y + g.outside.image.h };
    }
    return { x: g.outside.image.x, y: g.outside.image.y, w: g.outside.image.w, h: g.outside.image.h };
  }

  function preserveAspect(fit) {
    if (fit === 'stretch') return 'none';
    if (fit === 'fit') return 'xMidYMid meet';
    return 'xMidYMid slice';
  }

  function drawCover(svg, state, idPrefix) {
    const box = coverBox(state);
    const hasImage = Boolean(state.cover.src);

    if (!hasImage) {
      // Placeholder only ever appears on screen; the exporter drops it.
      const ph = el('rect', {
        x: box.x, y: box.y, width: box.w, height: box.h,
        fill: '#EFF4F7', stroke: state.colors.rules, 'stroke-width': 0.2,
        class: 'jc-placeholder',
      });
      svg.appendChild(ph);
      [
        { text: 'IMAGE PLACEHOLDER', dy: -1.2, size: 2.2 },
        { text: 'CHOOSE A FILE IN THE PANEL', dy: 2.4, size: 1.7 },
      ].forEach((line) => {
        const label = textNode({
          x: box.x + box.w / 2, y: box.y + box.h / 2 + line.dy, size: line.size,
          font: 'Archivo', weight: 400, color: '#8FA3B0', anchor: 'middle',
          text: line.text, tracking: 0.08,
        });
        label.classList.add('jc-placeholder');
        svg.appendChild(label);
      });
      return;
    }

    const clipId = `${idPrefix}-cover-clip`;
    const defs = el('defs', {});
    const clip = el('clipPath', { id: clipId });
    clip.appendChild(el('rect', { x: box.x, y: box.y, width: box.w, height: box.h }));
    defs.appendChild(clip);
    svg.appendChild(defs);

    const img = el('image', {
      x: box.x, y: box.y, width: box.w, height: box.h,
      preserveAspectRatio: preserveAspect(state.cover.fit),
      'clip-path': `url(#${clipId})`,
    });
    img.setAttribute('href', state.cover.src);
    img.setAttributeNS(XLINK, 'xlink:href', state.cover.src);
    svg.appendChild(img);

    if (state.cover.placement === 'inset') {
      svg.appendChild(el('rect', {
        x: box.x, y: box.y, width: box.w, height: box.h,
        fill: 'none', stroke: state.colors.rules, 'stroke-width': 0.2,
      }));
    }
  }

  function buildOutside(state, opts) {
    const g = JCARD.geom;
    const o = g.outside;
    const options = opts || {};
    const svg = svgRoot(g.CARD_W, g.CARD_H);
    svg.dataset.page = 'outside';

    background(svg, state, g.CARD_W, g.CARD_H);
    drawCover(svg, state, options.idPrefix || 'p1');

    // Full-panel artwork moves the title block down over the image, but the
    // text keeps whatever colours the fields carry — same as the other modes.
    const overlaying = state.cover.placement === 'full';
    const showText = !overlaying || state.cover.overlayText;

    // Flap.
    rotatedLines(svg, state.fields.flapLabel, 'flapLabel', o.flapLabel,
      o.textWidths.flap, o.textWidths.flapCross);
    if (state.fields.attributionFront.text.trim()) {
      rotatedLines(svg, state.fields.attributionFront, 'attributionFront', o.attribution,
        o.textWidths.flap, o.textWidths.flapCross);
    }

    // Spine.
    if (state.toggles.spineBars) {
      const bx = g.OUT.spineX + (g.SPINE_W - o.spineBar.w) / 2;
      svg.appendChild(el('rect', {
        x: bx, y: o.spineBar.topY, width: o.spineBar.w, height: o.spineBar.h, fill: state.colors.bars,
      }));
      svg.appendChild(el('rect', {
        x: bx, y: o.spineBar.bottomY, width: o.spineBar.w, height: o.spineBar.h, fill: state.colors.bars,
      }));
    }
    rotatedLines(svg, state.fields.spineText, 'spineText', o.spineText,
      state.toggles.spineBars ? o.textWidths.spine : o.textWidths.flap,
      o.textWidths.spineCross);

    // Cover text block.
    if (showText) {
      const left = o.albumTitle.x;
      const maxWidth = g.COVER_W - 2 * o.coverMargin;
      const ruleY = overlaying ? o.overlayRuleY : o.dividerRule.y;

      if (state.toggles.dividerRule) {
        svg.appendChild(el('rect', {
          x: o.dividerRule.x, y: ruleY, width: o.dividerRule.w, height: o.dividerRule.h,
          fill: state.colors.bars,
        }));
      }

      const single = (field, id, y) => {
        const line = linesOf(field, id)[0] || '';
        if (!line) return;
        svg.appendChild(textNode(Object.assign({}, field, {
          text: line,
          size: fit(field, line, maxWidth),
          x: left, y, anchor: 'start', color: field.color, field: id,
        })));
      };

      single(state.fields.albumTitle, 'albumTitle', overlaying ? o.overlayTitleY : o.albumTitle.y);
      single(state.fields.artistName, 'artistName', overlaying ? o.overlayArtistY : o.artistName.y);

      /* The one field allowed to run to several lines. They stack downward
         from the first baseline, so the block is bounded by whatever room is
         left above the bottom edge of the card. */
      const extra = state.fields.additionalText;
      const extraTop = overlaying ? o.overlayFormatY : o.formatLine.y;
      const extraLines = linesOf(extra, 'additionalText');
      const room = g.CARD_H - o.bottomMargin - extraTop;
      const blockSize = fitBlock(extra, extraLines.length, room, 0.3);
      layoutLines(extra, extraLines, maxWidth, blockSize).forEach((item, i) => {
        if (!item.line) return;
        svg.appendChild(textNode(Object.assign({}, extra, {
          text: item.line,
          size: item.size,
          x: left,
          y: extraTop + i * blockSize * g.inside.lineHeight,
          anchor: 'start', color: extra.color,
          field: i === 0 ? 'additionalText' : null,
        })));
      });
    }

    if (options.guides !== false) foldGuides(svg, g.foldsOutside, g.CARD_H);
    return svg;
  }

  /* ---------- page 2: back / inside ---------- */

  function trackLabel(field, index) {
    const raw = (field.lines[index] || '').trim();
    if (!raw) return '';
    return field.numbered ? `${index + 1}. ${raw}` : raw;
  }

  function drawBlock(svg, state, block, field) {
    const g = JCARD.geom;
    const ins = g.inside;
    const span = ins.columns.right - ins.columns.left;
    const step = span / (ins.columns.count - 1);
    const height = block.bottom - block.top;

    for (let i = 0; i < ins.columns.count; i++) {
      const x = ins.columns.left + i * step;
      svg.appendChild(el('line', {
        x1: x, y1: block.top, x2: x, y2: block.bottom,
        stroke: state.colors.rules, 'stroke-width': 0.2,
      }));

      if (field.mode === 'text') {
        // Track 1 belongs at the column nearest the A/B badge and the spine,
        // running outward to track 12 at the far edge — so the columns are
        // filled right to left.
        const label = trackLabel(field, ins.columns.count - 1 - i);
        if (label) {
          svg.appendChild(textNode({
            text: label,
            x: x + ins.trackText.offset,
            y: block.top + 1.2,
            rot: 90,
            font: field.font, weight: field.weight,
            size: fit(field, label, height - 2.4),
            tracking: field.tracking, color: field.color, anchor: 'start',
            field: `tracks${block.side}`,
          }));
        }
      }
    }

    if (state.toggles.sideBars) {
      svg.appendChild(el('rect', {
        x: ins.sideBar.x, y: block.top, width: ins.sideBar.w, height: height, fill: state.colors.bars,
      }));
    }

    // The disc and its letter travel together — a blank disc says nothing.
    if (state.toggles.sideBadges) {
      const badgeCy = block.top + ins.badge.dy;
      svg.appendChild(el('circle', {
        cx: ins.badge.cx, cy: badgeCy, r: ins.badge.r, fill: state.colors.bars,
      }));
      // Optically centre the letter by hand rather than leaning on
      // dominant-baseline, which the PDF outliner has no equivalent for.
      svg.appendChild(textNode({
        text: block.side,
        x: ins.badge.cx - ins.badge.size * 0.35, y: badgeCy, rot: 90,
        font: 'Archivo', weight: 700, size: ins.badge.size,
        tracking: 0, color: state.colors.badgeText, anchor: 'middle',
      }));
    }
  }

  function buildInside(state, opts) {
    const g = JCARD.geom;
    const ins = g.inside;
    const options = opts || {};
    const svg = svgRoot(g.CARD_W, g.CARD_H);
    svg.dataset.page = 'inside';

    background(svg, state, g.CARD_W, g.CARD_H);

    drawBlock(svg, state, ins.blocks[0], state.fields.tracksA);
    drawBlock(svg, state, ins.blocks[1], state.fields.tracksB);

    if (state.toggles.blockDivider) {
      svg.appendChild(el('line', {
        x1: ins.blockDivider.x1, y1: ins.blockDivider.y,
        x2: ins.blockDivider.x2, y2: ins.blockDivider.y,
        stroke: state.colors.rules, 'stroke-width': 0.2,
      }));
    }

    rotatedLines(svg, state.fields.insideSpine, 'insideSpine', ins.spineText,
      ins.textWidths.spine, ins.textWidths.spineCross);
    rotatedLines(svg, state.fields.credits, 'credits', ins.credits,
      ins.textWidths.flap, ins.textWidths.flapCross);

    if (state.fields.attributionBack.text.trim()) {
      rotatedLines(svg, state.fields.attributionBack, 'attributionBack', ins.attribution,
        ins.textWidths.flap, ins.textWidths.flapCross);
    }

    if (options.guides !== false) foldGuides(svg, g.foldsInside, g.CARD_H);
    return svg;
  }

  return { buildOutside, buildInside, el, textNode, fit, linesOf, NS };
})();
